import { TreeCard } from "@/components/molecules/TreeCard";
import { TreeTableView } from "@/components/molecules/TreeTableView";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateTreeButton } from "../components/atoms/CreateTreeButton";
import { NetworkErrorDisplay } from "../components/atoms/NetworkErrorDisplay";
import { PageTitle } from "../components/atoms/PageTitle";
import { useAuth } from "../contexts/AuthContext";
import { useTreeListStore } from "../lib/stores/treeListStore";
import { startSubscriptionPolling } from "../lib/utils/subscriptionPolling";
//import { treeUtils } from '@/lib/services/treeUtils';
//import { useTreeStore } from '../lib/stores/treeStore';

const DecisionTreeList: React.FC = () => {
  // View state - 'cards' or 'table'
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [isPolling, setIsPolling] = useState(false);
  const [showRefreshHint, setShowRefreshHint] = useState(false);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);

  // Use the tree list store to fetch and manage tree list
  const { trees, loading, error, fetchTrees, deleteTree } = useTreeListStore();

  const navigate = useNavigate();
  const location = useLocation();
  const {
    hasActiveSubscription,
    subscriptionError,
    refreshAuth,
    refreshSubscription,
  } = useAuth();

  // Handle subscription verification polling
  useEffect(() => {
    const navState: any = location.state || {};
    const shouldVerify = navState?.verifySubscription;

    if (shouldVerify && !hasActiveSubscription) {
      setIsPolling(true);
      setShowRefreshHint(false);

      // Show refresh hint after 20 seconds
      const hintTimer = setTimeout(() => {
        setShowRefreshHint(true);
      }, 20000);

      const cleanup = startSubscriptionPolling(
        async () => {
          // Success! Subscription verified
          clearTimeout(hintTimer);
          setShowRefreshHint(false);

          // Refresh auth first to update subscription status
          await refreshAuth();

          // Then refresh trees list
          await fetchTrees(true);

          // Finally stop polling (this ensures UI updates with new data)
          setIsPolling(false);
        },
        (error) => {
          // Polling failed or timed out
          console.error(
            "[DecisionTreeList] Polling failed or timed out:",
            error,
          );
          clearTimeout(hintTimer);
          setIsPolling(false);
          setShowRefreshHint(false);
          setPollingTimedOut(true);
        },
        (message, attempt) => {
          // Progress callback - we're not showing detailed messages
          console.log(
            `[DecisionTreeList] Progress: ${message} (attempt ${attempt})`,
          );
        },
      );

      // Clear the navigation state to prevent re-polling on refresh
      window.history.replaceState({}, document.title);

      return () => {
        clearTimeout(hintTimer);
        cleanup();
      };
    }
  }, [location.state, hasActiveSubscription, refreshAuth, fetchTrees]);

  // Load trees when component mounts or subscription status changes
  useEffect(() => {
    if (!isPolling) {
      fetchTrees(true); // Force refresh on initial load
    }
  }, [fetchTrees, isPolling, hasActiveSubscription]);

  // Handle tree deletion
  const handleDelete = async (id: string) => {
    try {
      await deleteTree(id);
    } catch (error) {
      console.error("Error deleting tree:", error);
    }
  };

  const handleEdit = (id: string) => {
    //loadTree(id);
    navigate(`/editor?treeId=${id}`); // `/editor/${id}`
  };

  // Force refresh the tree list from backend
  const handleRefresh = () => {
    fetchTrees(true);
  };

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newViewMode: "cards" | "table",
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Render the main content area based on current state
  const renderContent = () => {
    // PRIORITY 1: Network errors (check FIRST before anything else)
    // If we can't reach the backend, we shouldn't show "no subscription" messages
    if (subscriptionError) {
      return (
        <NetworkErrorDisplay
          errorMessage={subscriptionError}
          onRetry={() => refreshSubscription()}
        />
      );
    }

    // PRIORITY 2: Subscription verification polling - show centered spinner
    if (isPolling) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6">
            Verifying subscription information...
          </Typography>
          {showRefreshHint && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", mt: 2 }}
            >
              Taking longer than expected? Try refreshing the page.
            </Typography>
          )}
        </Box>
      );
    }

    // PRIORITY 3: Polling timed out
    if (pollingTimedOut) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <Typography variant="body1" gutterBottom>
              Network Error
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {subscriptionError}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please check your internet connection and try again. If the
              problem persists, you may need to refresh the page or sign out and
              sign back in.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => refreshSubscription()}
                startIcon={<RefreshIcon />}
              >
                Retry Connection
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </Box>
          </Alert>
        </Box>
      );
    }

    // No active subscription (only show after confirming we can reach the backend)
    if (!hasActiveSubscription) {
      return (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You don't have an active subscription. Subscribe to create and manage
          decision trees.{" "}
          <Button
            color="inherit"
            variant="outlined"
            size="small"
            href="/subscription"
            sx={{ ml: 1 }}
          >
            Subscribe Now
          </Button>
        </Alert>
      );
    }

    // Loading trees
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Error loading trees
    if (error) {
      return (
        <Typography color="error" align="center">
          {error}
        </Typography>
      );
    }

    // No trees yet
    if (trees.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No decision workspaces yet
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Create your first decision workspace to get started.
          </Typography>
          <CreateTreeButton
            variant="contained"
            sx={{ mt: 2 }}
            disabled={!hasActiveSubscription}
          />
        </Box>
      );
    }

    // Show trees in card or table view
    if (viewMode === "cards") {
      return (
        <Grid container spacing={3} key={trees.length}>
          {trees.map((tree) => (
            <Grid key={tree.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <TreeCard
                id={tree.id}
                filename={tree.filename}
                updatedAt={tree.updatedAt || "Unknown"}
                createdAt={tree.createdAt || "Unknown"}
                canDelete={tree.canDelete}
                onEdit={handleEdit}
                onDelete={handleDelete}
                disabled={!hasActiveSubscription}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <TreeTableView
        trees={trees}
        onEdit={(id: string) => {
          if (hasActiveSubscription) handleEdit(id);
        }}
        onDelete={(id: string) => {
          if (hasActiveSubscription) handleDelete(id);
        }}
      />
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 6 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <PageTitle
            title="Your Decision Workspaces"
            subtitle="Create and manage your decision workspaces to make better decisions"
          />
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="table" aria-label="table view">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="cards" aria-label="card view">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <CreateTreeButton
              variant="contained"
              sx={{ minWidth: 200 }}
              disabled={!hasActiveSubscription}
            />
          </Box>
        </Box>
      </Box>

      {renderContent()}
    </Container>
  );
};

export default DecisionTreeList;
